/**
 * As vendas da IsabellA — o controle do topo (Douglas 26/09/2026).
 *
 * Fonte única: RPC `kpi_vendas_ia`, que lê o mapa `v_isabella_vendas_linhas`.
 * O chip, o tooltip e o modal derivam TODOS os números desta lista — nenhum
 * total vem de fonte paralela.
 *
 * Régua (a mesma do mapa):
 *  - venda   = um registro da IsabellA (registrar_venda_fechada)
 *  - dela    = não anulada e não era pedido do consultor antes do registro
 *  - pedido  = uma linha da venda virou pedido lançado DEPOIS do registro
 *  - categoria conta LINHAS: uma venda pode ter TT e fibra ao mesmo tempo
 */

export type SituacaoLinhaVendaIa = 'ANULADA' | 'JA_ERA_PEDIDO' | 'VIROU_PEDIDO' | 'SEM_NUMERO' | 'SEM_PEDIDO';

export interface LinhaVendaIa {
  categoria: string;
  situacao: SituacaoLinhaVendaIa;
  pedido: number | string | null;
  quadro: string | null;
  coluna: string | null;
}

export interface VendaIa {
  venda_id: string;
  registrada_em: string;
  dia: string;
  cnpj: string | null;
  empresa: string | null;
  consultor: string | null;
  conversa_id: string | null;
  dela: boolean;
  anulada: boolean;
  valor: number | null;
  linhas: LinhaVendaIa[];
}

export type EstadoVendaIa = 'PEDIDO_ATIVO' | 'PEDIDO_ANDAMENTO' | 'PEDIDO_CANCELADO' | 'ESPERANDO' | 'NAO_ERA_DELA';

export const CATEGORIAS_VENDA_IA = [
  { chave: 'ALTAS', rotulo: 'Altas' },
  { chave: 'FIBRA', rotulo: 'Fibra' },
  { chave: 'TT', rotulo: 'TT' },
  { chave: 'RENOVACAO', rotulo: 'Renovação' },
] as const;

export function estadoDaVendaIa(v: VendaIa): EstadoVendaIa {
  if (!v.dela) return 'NAO_ERA_DELA';
  const viradas = v.linhas.filter((l) => l.situacao === 'VIROU_PEDIDO');
  if (!viradas.length) return 'ESPERANDO';
  if (viradas.some((l) => l.quadro === 'QUALIDADE')) return 'PEDIDO_ATIVO';
  if (viradas.some((l) => l.quadro && l.quadro !== 'PEDIDO_CANCELADO')) return 'PEDIDO_ANDAMENTO';
  return 'PEDIDO_CANCELADO';
}

export function virouPedido(estado: EstadoVendaIa): boolean {
  return estado === 'PEDIDO_ATIVO' || estado === 'PEDIDO_ANDAMENTO' || estado === 'PEDIDO_CANCELADO';
}

export interface ResumoPeriodoVendaIa {
  /** vendas DELA no período */
  vendas: number;
  /** das vendas dela, quantas viraram pedido */
  pedido: number;
  /** das vendas dela, quantas ainda não têm pedido */
  esperando: number;
  /** registros que não eram dela (anulados ou pedido do consultor antes) */
  naoEraDela: number;
}

export interface CategoriaVendaIa {
  chave: string;
  rotulo: string;
  linhas: number;
  pedido: number;
}

function somarDias(diaIso: string, dias: number): string {
  const d = new Date(`${diaIso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + dias);
  return d.toISOString().slice(0, 10);
}

export function inicioDoMes(diaIso: string, mesesAtras = 0): string {
  const [a, m] = diaIso.split('-').map(Number);
  const d = new Date(Date.UTC(a, m - 1 - mesesAtras, 1, 12));
  return d.toISOString().slice(0, 10);
}

export function resumirPeriodo(vendas: VendaIa[]): ResumoPeriodoVendaIa {
  return vendas.reduce<ResumoPeriodoVendaIa>((acc, v) => {
    const estado = estadoDaVendaIa(v);
    if (estado === 'NAO_ERA_DELA') return { ...acc, naoEraDela: acc.naoEraDela + 1 };
    return {
      ...acc,
      vendas: acc.vendas + 1,
      pedido: acc.pedido + (virouPedido(estado) ? 1 : 0),
      esperando: acc.esperando + (estado === 'ESPERANDO' ? 1 : 0),
    };
  }, { vendas: 0, pedido: 0, esperando: 0, naoEraDela: 0 });
}

export function categoriasDasVendas(vendas: VendaIa[]): CategoriaVendaIa[] {
  const linhas = vendas.filter((v) => v.dela).flatMap((v) => v.linhas)
    .filter((l) => l.situacao !== 'ANULADA' && l.situacao !== 'JA_ERA_PEDIDO');
  return CATEGORIAS_VENDA_IA.map((c) => ({
    chave: c.chave,
    rotulo: c.rotulo,
    linhas: linhas.filter((l) => l.categoria === c.chave).length,
    pedido: linhas.filter((l) => l.categoria === c.chave && l.situacao === 'VIROU_PEDIDO').length,
  }));
}

export function vendasDoMes(vendas: VendaIa[], hoje: string, mesesAtras = 0): VendaIa[] {
  const de = inicioDoMes(hoje, mesesAtras);
  const ate = inicioDoMes(hoje, mesesAtras - 1);
  return vendas.filter((v) => v.dia >= de && v.dia < ate);
}

export function resumirVendasIa(vendas: VendaIa[], hoje: string) {
  const ontem = somarDias(hoje, -1);
  const seteDias = somarDias(hoje, -6);
  const doMes = vendasDoMes(vendas, hoje);
  return {
    hoje: resumirPeriodo(vendas.filter((v) => v.dia === hoje)),
    ontem: resumirPeriodo(vendas.filter((v) => v.dia === ontem)),
    semana: resumirPeriodo(vendas.filter((v) => v.dia >= seteDias && v.dia <= hoje)),
    mes: resumirPeriodo(doMes),
    categoriasMes: categoriasDasVendas(doMes),
  };
}
